import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Switch,
  Flex,
  Select,
  Box,
  IconButton,
  ModalFooter,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { createAPIEndPoint } from 'config/api/api';
import toast from 'react-hot-toast';
import { getUserData } from 'utils';
import { MdClose } from 'react-icons/md';

const OptionsTreatmentAreaModal = ({
  isOpen,
  onClose,
  type = 'add',
  initialData = {},
  fetchAgain,
  treatment_area_id
}) => {
  console.log(" initialData:", initialData)
  const userData = getUserData();
  const clinic_id = userData.clinic_id ?? null;
  const [additionalValues, setAdditionalValues] = useState([""]); // Start with one input by default


  // Inside your component
  useEffect(() => {
    if (isOpen) {
      if (type === 'edit') {
        // 🟢 Reset formik values manually
        formik.setValues({
          optionName: initialData?.option_name || '',
          optionType: initialData?.option_type || '',
          isEnabled: initialData?.status === 'active',
        });

        // 🟢 Set additional values
        setAdditionalValues(initialData?.values?.length ? initialData.values : ['']);
      } else {
        // 🧹 If it's "add", reset everything cleanly
        formik.resetForm();
        setAdditionalValues(['']);
      }
    }
  }, [isOpen, type, initialData]);


  const handleClose = () => {
    formik.resetForm();
    setAdditionalValues([""]);
    formik.setFieldValue('isEnabled', false);
    onClose(); // closes the modal
  };


  // Function to handle adding new values
  const handleAddValue = () => {
    setAdditionalValues([...additionalValues, ""]); // Add an empty string to represent a new input
  };

  // Function to update a specific additional value
  const handleValueChange = (index, value) => {
    const updatedValues = [...additionalValues];
    updatedValues[index] = value;
    setAdditionalValues(updatedValues);
  };

  // Function to remove a value
  const handleRemoveValue = (index) => {
    const updatedValues = additionalValues.filter((_, i) => i !== index);
    setAdditionalValues(updatedValues);
  };



  const validationSchema = Yup.object().shape({
    optionName: Yup.string().required('Option name is required'),
    optionType: Yup.string().required('Option type is required'),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      optionName: initialData?.option_name || '',
      optionType: initialData?.option_type || 'Text',
      isEnabled: initialData?.status === 'active',
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        treatment_id: treatment_area_id,
        clinic_id,
        treatment_area: values.treatmentArea,
        options: [
          {
            option_id: initialData?.option_id,
            option_name: values.optionName,
            option_type: values.optionType,
            status: values.isEnabled ? 'active' : 'inactive',
            values: additionalValues.filter(val => val.trim() !== ""),
          }
        ]
      };

      try {
        if (initialData.option_id) {
          await createAPIEndPoint('treatment_option/').update(
            initialData.option_id,
            payload,
          );
          toast.success('Treatment area updated successfully!');
        } else {
          await createAPIEndPoint('create_treatment_option').create(payload);
          toast.success('Treatment area added successfully!');
          resetForm();
        }
        fetchAgain();
        onClose();
      } catch (error) {
        toast.error(
          error?.response?.data?.error ||
          'An error occurred while processing the treatment area.',
        );
      }
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent
        borderRadius="16px"
        pb="16px"
        maxWidth={{ base: '90%', md: '448px' }}
        maxH="90vh"
        overflowY="auto"
      >
        <ModalHeader color="brand.500">
          {type === 'edit' ? 'Edit Treatment Option' : 'Add Treatment Option'}
        </ModalHeader>
        <ModalCloseButton />
          <form onSubmit={formik.handleSubmit}>
        <ModalBody maxHeight="70vh" overflowY="auto" pb={2}>
            {[{ name: 'optionName', label: 'Option Name' }].map(
              ({ name, label }) => (
                <FormControl
                  key={name}
                  isInvalid={formik.touched[name] && formik.errors[name]}
                  mb={3}
                >
                  <FormLabel mb={0}>{label}</FormLabel>
                  <Input
                    name={name}
                    placeholder={`Enter ${label}`}
                    variant="auth"
                    fontSize="sm"
                    fontWeight="500"
                    value={formik.values[name]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched[name] && formik.errors[name] && (
                    <Text color="red.500" fontSize="sm">
                      {formik.errors[name]}
                    </Text>
                  )}
                </FormControl>
              ),
            )}

            <FormControl
              isInvalid={formik.touched.optionType && formik.errors.optionType}
              mb={3}
            >
              <FormLabel mb={0}>Option Type</FormLabel>
              <Select
                name="optionType"
                variant="auth"
                fontSize="sm"
                value={formik.values.optionType}
                onChange={(e) => {
                  formik.handleChange(e);
                  setAdditionalValues([""]); // Reset additional values when switching type
                }}
                onBlur={formik.handleBlur}
              >
                {/* <option value="" disabled>Select Option Type</option> */}
                <option value="Text">Text</option>
                <option value="Dropdown">Dropdown</option>
                <option value="Checkbox">Checkbox</option>
              </Select>
              {formik.touched.optionType && formik.errors.optionType && (
                <Text color="red.500" fontSize="sm">
                  {formik.errors.optionType}
                </Text>
              )}
            </FormControl>

            {formik.values.optionType !== 'Text' && (
              <>
                <Box mt={3}>
                  {additionalValues.map((value, index) => (
                    <Flex key={index} alignItems="center" mb={2}>
                      <Input
                        placeholder="Enter value"
                        value={value}
                        onChange={(e) => handleValueChange(index, e.target.value)}
                        variant="auth"
                        fontSize="sm"
                        fontWeight="500"
                      />
                      {additionalValues.length > 1 && (
                        <IconButton
                          icon={<MdClose />}
                          aria-label="Remove value"
                          colorScheme="red"
                          size="sm"
                          ml={2}
                          onClick={() => handleRemoveValue(index)}
                        />
                      )}
                    </Flex>
                  ))}
                  <Button
                    colorScheme="blue"
                    size="sm"
                    onClick={handleAddValue}
                    mt={2}
                  >
                    Add Another Value
                  </Button>
                </Box>
              </>
            )}


            {type === 'edit' && (
              <FormControl mt={4}>
                <FormLabel mb={2} fontWeight="600">Status</FormLabel>
                <Flex align="center" justify="space-between" p={2} bg="gray.50" borderRadius="md">
                  <Flex align="center" gap={3}>
                    <Switch
                      colorScheme="green"
                      size="md"
                      name="isEnabled"
                      isChecked={formik.values.isEnabled}
                      onChange={(e) => formik.setFieldValue('isEnabled', e.target.checked)}
                    />
                    <Text fontWeight="500" fontSize="sm">
                      {formik.values.isEnabled ? 'Enabled' : 'Disabled'}
                    </Text>
                  </Flex>
                  <Box
                    px={3}
                    py={1}
                    borderRadius="full"
                    bg={formik.values.isEnabled ? 'green.100' : 'gray.200'}
                    color={formik.values.isEnabled ? 'green.800' : 'gray.600'}
                    fontSize="xs"
                    fontWeight="600"
                  >
                    {formik.values.isEnabled ? 'Active' : 'Inactive'}
                  </Box>
                </Flex>
              </FormControl>

            )}

        </ModalBody>
        <ModalFooter pt={0}>
          <Button type="submit" colorScheme="brand" w="100%" mt={4} isLoading={formik.isSubmitting}>
            {type === 'edit'
              ? 'Edit Treatment Option'
              : 'Add Treatment Option'}
          </Button>
        </ModalFooter>
              </form>
      </ModalContent>
    </Modal >
  );
};

export default OptionsTreatmentAreaModal;
